import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../config/api';
import { useAuth } from '../context/AuthContext';

interface KnowledgeResult {
  id: number;
  file_path: string;
  title: string;
  similarity: number;
  last_modified: string;
}

const baseUrl = API_BASE_URL;

async function fetchRelatedKnowledge(notifId: number): Promise<KnowledgeResult[]> {
  const res = await authFetch(`${baseUrl}/api/knowledge/for-notification/${notifId}`);
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { ok: boolean; results: KnowledgeResult[] };
  if (!data.ok) throw new Error('Failed to load related knowledge');
  return data.results;
}

function obsidianLink(filePath: string): string {
  const file = filePath.replace(/\.md$/, '');
  return `obsidian://open?vault=Obsidian&file=${encodeURIComponent(file)}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function RelatedKnowledge({ notifId }: { notifId: number }) {
  const { isAuthenticated } = useAuth();

  const { data: results, isLoading, isError } = useQuery<KnowledgeResult[]>(
    ['relatedKnowledge', notifId],
    () => fetchRelatedKnowledge(notifId),
    { enabled: isAuthenticated },
  );

  if (!isAuthenticated) return null;
  if (isLoading) return <div className="related-knowledge__loading">Finding related knowledge...</div>;
  if (isError || !results || results.length === 0) return null;

  const r = results[0];

  return (
    <div className="related-knowledge">
      <h4 className="related-knowledge__title">Related Knowledge</h4>
      <a
        className="knowledge-card"
        href={obsidianLink(r.file_path)}
        title={`Open in Obsidian: ${r.title}`}
      >
        <div className="knowledge-card__header">
          <span className="knowledge-card__title">{r.title}</span>
          <span className="knowledge-card__meta">
            <span className="knowledge-card__date">{formatDate(r.last_modified)}</span>
            <span className="similarity-badge">{Math.round(r.similarity * 100)}%</span>
          </span>
        </div>
      </a>
    </div>
  );
}
